<template>
	<ChalkyTrainerSceneFreeplay
		class="view-trainer-trainer"
		v-bind:class="{ 'state-ready': isReady }"
		v-bind:autoActivate="true"
		v-bind:environment="'Generic'"
		v-bind:forwardFoulToasts="true"
		v-bind:forwardMatchToasts="true"
		v-bind:forwardTrickToasts="true"
		v-bind:freelook="true"
		v-bind:gameType="'NineBall'"
		v-bind:showEnglishMeter="true"
		v-bind:showPowerMeter="true"
		v-bind:tableAppearance="'Traditional'"
		v-bind:tableOverlays="['overlay-breakbox']"
		v-on:game:ready="Handle_OnWorldReady"
	></ChalkyTrainerSceneFreeplay>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import ChalkySticks, { type IDispatcherEvent } from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component } from 'vue-property-decorator';
	import { Event as TrainerEvent } from '@chalkysticks/vue2-trainer';
	import { beforeDestroy, bind, mounted } from '@/Utility/Decorators';

	/**
	 * Async component definition so the trainer runtime stays out of the app
	 * entry chunk. The function dynamically imports the trainer components
	 * module and resolves to the named export, ensuring the concrete component
	 * is in hand before Vue tries to render the tag.
	 */
	const asyncComponents = {
		ChalkyTrainerSceneFreeplay: () =>
			import(
				/* webpackChunkName: "trainer-freeplay" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.SceneFreeplay),
	};

	/**
	 * Quest host for the ChalkySticks trainer.
	 *
	 * Renders <SceneFreeplay> (which wraps <SceneShell> → <SceneGame>, so the
	 * shell owns the "Enter VR" button and HUD chrome). Plain freeplay only —
	 * no match-config plumbing.
	 *
	 * When the app is launched as an installed PWA on a Quest headset it tries
	 * to jump straight into immersive VR — at the EARLIEST moment the trainer's
	 * XR controller allows it (when XR reports ready + supported), rather than
	 * waiting for the later game:ready. We listen on XR.StateChanged instead of
	 * game:ready because entry can only succeed once `ready` is true, and the
	 * sooner we fire after launch the better the chance of landing inside the
	 * headset's launch-activation window for WebXR. In a normal browser tab the
	 * visible "Enter VR" button stays the entry point.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Trainer
	 * @project ChalkySticks Quest
	 */
	@Component({
		components: {
			...asyncComponents,
		},
	})
	export default class ViewTrainerTrainer extends ViewBase {
		/**
		 * One-shot guard so we auto-enter VR only on the first opportunity and
		 * never fight the user if they deliberately exit VR (which fires more
		 * XR.StateChanged events).
		 *
		 * @type boolean
		 */
		protected hasRequestedImmersive: boolean = false;

		/**
		 * Flips true once the world reports ready; drives the fade-in.
		 *
		 * @type boolean
		 */
		protected isReady: boolean = false;

		/**
		 * Whether the app is running as an installed PWA (launched from the
		 * headset library / home screen) rather than inside a browser tab. The
		 * display-mode media query only matches the manifest's display value
		 * once the app has been installed and launched standalone.
		 *
		 * @return boolean
		 */
		protected get isInstalledPwa(): boolean {
			return window.matchMedia('(display-mode: fullscreen)').matches || window.matchMedia('(display-mode: standalone)').matches;
		}

		/**
		 * Subscribe to XR state changes as early as possible (before the async
		 * scene resolves) so we don't miss the ready transition.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on(TrainerEvent.XR.StateChanged, this.Handle_OnXRStateChanged);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off(TrainerEvent.XR.StateChanged, this.Handle_OnXRStateChanged);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Auto-enter immersive VR the moment the trainer reports it can. The XR
		 * controller only honors an enter request once it is both `ready` (the
		 * table model has loaded) and `supported` (a headset is present), so
		 * this is the earliest entry can succeed — earlier than game:ready,
		 * which additionally waits for the practice match to auto-start.
		 *
		 * Gated to an installed Quest PWA; a browser tab keeps the manual
		 * button. Reuses the same event the shell's "Enter VR" button
		 * dispatches, so that button remains the fallback if the runtime
		 * declines the session (e.g. the launch activation has expired).
		 *
		 * @param IDispatcherEvent<IXRState> e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnXRStateChanged(e: IDispatcherEvent<{ active: boolean; ready: boolean; supported: boolean }>): Promise<void> {
			const state = e.data;

			// Already in VR, or we've already fired our one-shot request.
			if (this.hasRequestedImmersive || state.active) {
				return;
			}

			// Wait until immersive entry can actually succeed.
			if (!state.ready || !state.supported) {
				return;
			}

			// Only auto-launch on an installed Quest PWA.
			if (!Config.Environment.IS_QUEST_BROWSER || !this.isInstalledPwa) {
				return;
			}

			this.hasRequestedImmersive = true;

			ChalkySticks.Core.Event.Bus.dispatch(TrainerEvent.XR.EnterRequest);
		}

		/**
		 * Handle when the world (3D scene + physics) reports ready.
		 *
		 * @param any gameInstance
		 * @return Promise<void>
		 */
		protected async Handle_OnWorldReady(gameInstance: any): Promise<void> {
			// Fade in
			setTimeout(() => (this.isReady = true), 250);

			// Bubble up so the host page can react if needed
			this.$emit('game:ready', gameInstance);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-trainer-trainer {
		--english-size: 100px;

		height: 100%;
		opacity: 0;
		transition: opacity 0.5s ease-in-out;
		width: 100%;

		// State
		// ---------------------------------------------------------------------------

		&.state-ready {
			opacity: 1;
		}
	}
</style>
